import {ApiResponse as BaseResponse} from '../models/api.models';
import {DataService} from '../services/data.service';
import {environment} from '../../environments/environment';
import {ChangeDetectionStrategy, Component, inject, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {finalize} from 'rxjs';
import {formatLogMessage, LogMessagePart} from './log-message';

interface Catalog extends BaseResponse { Applications: string[]; Modules: string[]; Levels: string[]; ModulesByApplication: Record<string, string[]>; }
interface Entry {Timestamp: string; Level: string; Application: string; Module: string; Message: string; EntryNumber: number; IsMatch: boolean; Truncated: boolean; MessageParts?: LogMessagePart[];}
interface Results extends BaseResponse {Entries: Entry[]; TotalMatches: number; TotalPages: number; Page: number; PageSize: number; ScannedFiles: number;}

@Component({selector: 'app-logs', standalone: false, templateUrl: './logs.component.html', styleUrls: ['./logs.component.css'], changeDetection: ChangeDetectionStrategy.Eager})
export class LogsComponent implements OnInit {
    private readonly http = inject(HttpClient);
    private readonly data = inject(DataService);
    private get headers() { return {Token: this.data.getSessionIdentifier()}; }
    day = (() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    })();
    fromHour = 0;
    toHour = 24;
    context = 2;
    pageSize = 50;
    pageSizeSelection: number | 'custom' = 50;
    readonly pageSizes = [10, 25, 50, 100];
    readonly fromHours = Array.from({length: 24}, (_, hour) => hour);
    readonly toHours = Array.from({length: 24}, (_, hour) => hour + 1);

    changePageSizeSelection(): void {
        if (this.pageSizeSelection !== 'custom') this.pageSize = this.pageSizeSelection;
    }

    text = '';
    application = '';
    modules: string[] = [];
    levels: string[] = [];
    catalog: Catalog = {StatusCode: 0, StatusMessage: '', Applications: [], Modules: [], ModulesByApplication: {}, Levels: ['VRB', 'DBG', 'INF', 'WRN', 'ERR', 'FTL']};
    results?: Results;
    busy = false;
    error = '';
    private query?: object;
    readonly levelNames: Record<string, string> = {
        VRB: 'Verbose', VERBOSE: 'Verbose', TRACE: 'Trace',
        DBG: 'Debug', DEBUG: 'Debug',
        INF: 'Information', INFO: 'Information', INFORMATION: 'Information',
        WRN: 'Warning', WARN: 'Warning', WARNING: 'Warning',
        ERR: 'Error', ERROR: 'Error',
        FTL: 'Fatal', FATAL: 'Fatal'
    };
    levelName(level: string): string {return this.levelNames[level.toUpperCase()] || level;}
    ngOnInit() {this.loadCatalog();}
    get availableModules(): string[] {
        return this.application ? this.catalog.ModulesByApplication?.[this.application] || [] : this.catalog.Modules;
    }
    applicationChanged() {
        this.modules = this.modules.filter(module => this.availableModules.includes(module));
    }
    loadCatalog() {
        this.application = ''; this.modules = []; this.results = undefined; this.query = undefined;
        this.catalog = {...this.catalog, Applications: [], Modules: [], ModulesByApplication: {}};
        this.http.get<Catalog>(`${environment.baseUrl}/soc-admin/v1/logs-catalog`, {headers: this.headers, params: {day: this.day}}).subscribe({
            next: value => {if (value.StatusCode !== 0) { this.error = value.StatusMessage || 'Request could not be completed.'; return; } this.error = ''; this.catalog = value;}, error: error => this.failure(error)
        });
    }
    search(page = 1, newSearch = true) {
        if (this.busy) return;
        if (newSearch) this.query = {Day: this.day, FromHour: this.fromHour, ToHour: this.toHour, Context: this.context,
            PageSize: this.pageSize, Text: this.text, Applications: this.application ? [this.application] : [], Modules: [...this.modules], Levels: [...this.levels]};
        if (!this.query) return;
        this.busy = true; this.error = '';
        this.http.post<Results>(`${environment.baseUrl}/soc-admin/v1/logs-search`, {...this.query, Page: page}, {headers: this.headers}).pipe(finalize(() => this.busy = false)).subscribe({
            next: value => {
                if (value.StatusCode !== 0) { this.error = value.StatusMessage || 'Request could not be completed.'; return; }
                for (const entry of value.Entries) entry.MessageParts = formatLogMessage(entry.Message);
                this.results = value;
            }, error: error => this.failure(error)
        });
    }
    levelAbbreviation(level: string): string {
        const name = this.levelName(level);
        return ({Verbose: 'VRB', Trace: 'VRB', Debug: 'DBG', Information: 'INF', Warning: 'WRN', Error: 'ERR', Fatal: 'FTL'} as Record<string, string>)[name] || level.toUpperCase();
    }

    logTime(timestamp: string): string {
        return timestamp.match(/[T ](\d{2}:\d{2}:\d{2}(?:[.:]\d{3})?)/)?.[1].replace(/:(\d{3})$/, '.$1') || timestamp;
    }

    goToPage(page: number): void {
        if (!this.results || this.busy || page < 1 || page > this.results.TotalPages || page === this.results.Page) return;
        this.search(page, false);
    }

    getFirstItemNumber(): number {
        return !this.results?.TotalMatches ? 0 : (this.results.Page - 1) * this.results.PageSize + 1;
    }

    getLastItemNumber(): number {
        return this.results ? Math.min(this.results.Page * this.results.PageSize, this.results.TotalMatches) : 0;
    }

    private failure(error: {error?: {StatusMessage?: string}}) {this.error = error.error?.StatusMessage || 'Request could not be completed.';}
}
